import dayjs from 'dayjs'
import type { AddDetourFormValues, OrderStopWithMeta, StopTypes } from './types'
import { uniqueId } from 'lodash'
import { SupplyOwnerMap } from '../ControlPane/PaneComponents/SupplyFilters/types'
import { ProductGroupsApiType } from '../../Contexts/OrderContainersContext/ProductGroups/useProductGroups'

export function disableFutureTimePickerDates(selectedDate: string | null) {
  if (selectedDate && dayjs(selectedDate).isSame(dayjs(), 'day')) {
    const now = dayjs()
    return {
      disabledHours: () => {
        const hours = []
        for (let i = now.hour() + 1; i < 24; i++) {
          hours.push(i)
        }
        return hours
      },
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = []
          for (let i = now.minute() + 1; i < 60; i++) {
            minutes.push(i)
          }
          return minutes
        }
        return []
      },
    }
  }
  return {}
}

export function sortOrderStops(
  loads: Gravitate.OrderLoad[] = [],
  detours: Gravitate.OrderDetour[] = [],
  drops: Gravitate.OrderDrop[] = []
): OrderStopWithMeta[] {
  // Add originalIndex to each item and combine lists
  const loadsWithIndex: OrderStopWithMeta[] = loads.map((load, index) => ({
    ...load,
    originalIndex: index,
    itemType: 'load' as const,
  }))

  const detoursWithIndex: OrderStopWithMeta[] = detours.map((detour, index) => ({
    ...detour,
    originalIndex: index,
    itemType: 'detour' as const,
  }))

  const dropsWithIndex: OrderStopWithMeta[] = drops.map((drop, index) => ({
    ...drop,
    originalIndex: index,
    itemType: 'drop' as const,
  }))

  const combinedList = [...detoursWithIndex, ...loadsWithIndex, ...dropsWithIndex]

  return combinedList.sort((a, b) => {
    // If both have null/undefined completed_at, detours should always have a completed_at
    if (!a.completed_at && !b.completed_at) {
      // Sort loads first
      if (a.itemType === 'load' && b.itemType === 'drop') return -1
      if (a.itemType === 'drop' && b.itemType === 'load') return 1

      // If both are same type, sort by originalIndex
      if (a.itemType === b.itemType) {
        return a.originalIndex - b.originalIndex
      }

      // Fallback, maintain current order
      return 0
    }

    // If one has null completed_at, put nulls at end
    if (!a.completed_at && b.completed_at) return 1
    if (a.completed_at && !b.completed_at) return -1

    // Both have completed_at timestamps, sort by timestamp (ascending)
    if (a.completed_at && b.completed_at) {
      return dayjs(a.completed_at).valueOf() - dayjs(b.completed_at).valueOf()
    }

    // Fallback (should not reach here)
    return 0
  })
}

export function getDetourPayload(
  formValues: AddDetourFormValues,
  driverName: string,
  driverScheduleId: string
): Gravitate.OrderDetour {
  const locationInfo = JSON.parse(formValues.location as string)
  return {
    type: 'no_load',
    driver: driverName,
    driver_schedule_id: driverScheduleId,
    location_id: locationInfo.id,
    location_name: locationInfo.name,
    address: locationInfo.address,
    city: locationInfo.city,
    state: locationInfo.state,
    postal_code: '',
    completed_at: dayjs(formValues.date)
      .startOf('day')
      .add(dayjs(formValues.time).hour(), 'hour')
      .add(dayjs(formValues.time).minute(), 'minute')
      .utc()
      .toISOString(),
    detour_id: '',
    temp_id: uniqueId(),
  }
}

export function isOrderEditable(
  lockedOrders: Gravitate.Order[],
  wizardOrder: Gravitate.Order,
  scopes: Partial<Gravitate.AuthorizationResponse>,
  isComplete: boolean
) {
  return (
    !lockedOrders?.map((order) => order.id).includes(wizardOrder.id) && // order is not locked
    scopes?.create_order && // user has create_order scope
    !wizardOrder.is_linked && // order is not linked to another driver
    wizardOrder.carrier_info?.crossroads_status !== 'assigned' && // order is not assigned to a carrier
    !isComplete // order is not complete
  )
}

export function showResequenceButtons(
  type: StopTypes,
  allowTerminalSwaps: boolean,
  showEditControls: boolean,
  stop: OrderStopWithMeta,
  isPrevComplete: boolean,
  length: number
) {
  return (
    type !== 'detour' && // cannot resequence detours
    (type === 'load' ? allowTerminalSwaps : true) && // loads can only be resequenced if terminal swaps are allowed
    showEditControls &&
    !stop.completed_at && // cannot resequence completed stops
    (isPrevComplete ? stop.originalIndex < length - 1 : true) // check if is only uncompleted stop in sequence
  )
}

export function allSupplyOptionsOnOrder(
  wizardOrder: Gravitate.Order,
  newCompartments: Gravitate.Order['compartments'],
  productGroups: ProductGroupsApiType['productGroups'],
  isByProduct: boolean
): boolean {
  const options = wizardOrder?.supply_option?.supply_owner_map || { definitions: [] }
  
  const productsChanged = haveProductsChanged(wizardOrder?.compartments || [], newCompartments, productGroups)
  if (productsChanged) return false

  const comps = isByProduct ? Object.values(productGroups).flat() : newCompartments
  const productStoreCombos = comps.flatMap((c) => {
    return c.targets.map((t) => {
      return {
        productName: c.product_name,
        storeNumber: t.location_name,
      }
    })
  })

  return productStoreCombos.every((combo) =>
    options.definitions.some((def) => def.product === combo.productName && def.location_name === combo.storeNumber)
  )
}

/** This is dumb but product groups only exists if its a by_product order, or else its empty.
 * So we need to check that first to see if it has anything in it, and if not then we take products
 * off of compartments. We can't use compartments alone because the compartments array is populated
 * even if the order is by_product.
 */
export function haveProductsChanged(
  oldCompartments: Gravitate.Order['compartments'],
  newCompartments: Gravitate.Order['compartments'], 
  productGroups: ProductGroupsApiType['productGroups']
): boolean {
  const productKeys = new Set<string>(Object.keys(productGroups))

  const oldProductSet = new Set(
    oldCompartments.filter(Boolean).map((c) => c.product_name.toLowerCase()))
  const newProductSet = productKeys.size ? productKeys : new Set(
    newCompartments.filter(Boolean).map((c) => c.product_name.toLowerCase()))

  if (oldProductSet.size !== newProductSet.size) {
    return true
  }

  for (const product of oldProductSet) {
    if (!newProductSet.has(product)) {
      return true
    }
  }

  return false
}